// components/Board.js

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Alert, Animated, Platform } from 'react-native';
import { Chess } from 'chess.js';
import PromotionModal from './PromotionModal';
import SvgPiece from './SvgPiece';
import PropTypes from 'prop-types';

// Conditional import for react-native-sound (must not break web bundling)
let Sound;
if (Platform.OS !== 'web') {
  try {
    // Prevent Webpack from trying to resolve react-native-sound during web builds.
    // eslint-disable-next-line no-eval
    const dynamicRequire = eval('require');
    const mod = dynamicRequire('react-native-sound');
    Sound = mod?.default ?? mod;

    if (Sound && typeof Sound.setCategory === 'function') {
      Sound.setCategory('Playback');
    }
  } catch (e) {
    console.warn('Failed to load react-native-sound', e);
  }
}

// Import the move sound
const moveSoundSrc = require('../assets/move.mp3'); // Ensure the path is correct

/**
 * Clone a chess.js instance without mutating the source.
 *
 * Preferred:
 * - Rebuild from verbose history when available (preserves last move).
 * Fallbacks:
 * - Try PGN load (preserves move list in most cases).
 * - Finally clone by FEN (position only).
 */
const cloneChessFrom = (srcGame) => {
  try {
    // 1) Best: replay verbose history
    if (srcGame && typeof srcGame.history === 'function') {
      const hist = srcGame.history({ verbose: true });

      if (Array.isArray(hist) && hist.length > 0) {
        const startFen = hist[0]?.before; // present in modern chess.js
        let g;

        try {
          g = startFen ? new Chess(startFen) : new Chess();
        } catch (e) {
          g = new Chess();
          if (startFen && typeof g.load === 'function') {
            try {
              g.load(startFen);
            } catch (_) {
              // ignore
            }
          }
        }

        for (const m of hist) {
          g.move({
            from: m.from,
            to: m.to,
            promotion: m.promotion,
          });
        }

        return g;
      }
    }

    // 2) Next best: load PGN
    if (srcGame && typeof srcGame.pgn === 'function') {
      const pgn = srcGame.pgn();
      if (pgn && typeof pgn === 'string') {
        const g = new Chess();
        if (typeof g.loadPgn === 'function') {
          const ok = g.loadPgn(pgn, { sloppy: true });
          if (ok) return g;
        }
      }
    }

    // 3) Fallback: FEN only (no history)
    if (srcGame && typeof srcGame.fen === 'function') {
      return new Chess(srcGame.fen());
    }
  } catch (e) {
    // fall through
  }

  return new Chess();
};

const Board = ({
  game: incomingGame,
  onMove,
  playerColor,
  mistakeSquare,
  hintSquares,
  isInteractive = true,
  allowSelectionWhenInactive = false,
}) => {
  /**
   * IMPORTANT:
   * - If parent provides `onMove` and updates `incomingGame`, we mirror it into local state.
   * - If parent does NOT provide `onMove` (e.g., OpeningExplorer), we must manage moves locally
   *   to trigger re-render. We therefore never mutate `incomingGame` directly.
   */
  const [game, setGame] = useState(() => cloneChessFrom(incomingGame));

  useEffect(() => {
    if (incomingGame) {
      setGame(cloneChessFrom(incomingGame));
    }
  }, [incomingGame]);

  const fen = useMemo(() => game.fen(), [game]);

  const [selectedSquare, setSelectedSquare] = useState(null);
  const [isPromotionVisible, setPromotionVisible] = useState(false);
  const [promotionOptions, setPromotionOptions] = useState({});

  const [mistakeOpacity] = useState(new Animated.Value(0));
  const [hintOpacity] = useState(new Animated.Value(0));
  const hintLoopRef = useRef(null);

  const [lastMove, setLastMove] = useState(null);

  // Ref used to avoid “late clears” wiping a user’s newer selection.
  const selectionNonceRef = useRef(0);

  const setSelection = (nextSquare) => {
    selectionNonceRef.current += 1;
    setSelectedSquare(nextSquare);
  };

  const clearSelection = () => {
    setSelection(null);
    setPromotionVisible(false);
    setPromotionOptions({});
  };

  // Refs for sound instances
  const webAudioPoolRef = useRef({ audioContext: null, buffer: null });
  const mobileSoundRef = useRef(null);

  // Define the starting FEN for comparison
  const startingFEN = useRef(new Chess().fen()).current;

  // Ref to store the previous FEN
  const prevFENRef = useRef(fen);

  // Load the sound once when the component mounts
  useEffect(() => {
    if (Platform.OS === 'web') {
      // Web platform: Initialize Web Audio API
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) {
        console.error('Web Audio API is not supported in this browser');
        return undefined;
      }
      const audioContext = new AudioContext();
      webAudioPoolRef.current.audioContext = audioContext;

      fetch(moveSoundSrc)
        .then((response) => response.arrayBuffer())
        .then((arrayBuffer) => audioContext.decodeAudioData(arrayBuffer))
        .then((audioBuffer) => {
          webAudioPoolRef.current.buffer = audioBuffer;
        })
        .catch((error) => {
          console.error('Error loading audio buffer:', error);
        });

      // Cleanup on unmount
      return () => {
        if (audioContext) {
          audioContext.close();
        }
      };
    }

    if (Sound) {
      // Mobile platform: Initialize Sound instance
      mobileSoundRef.current = new Sound(moveSoundSrc, Sound.MAIN_BUNDLE, (error) => {
        if (error) {
          console.error('Failed to load the move sound', error);
        }
      });
    }

    // Cleanup on unmount
    return () => {
      if (mobileSoundRef.current) {
        mobileSoundRef.current.release();
      }
    };
  }, []);

  // Keep selection sane across external game updates (e.g., trainer move)
  useEffect(() => {
    if (!selectedSquare) return;
    const p = game.get(selectedSquare);
    if (!p || p.color !== playerColor) {
      setSelection(null);
    }
  }, [fen, playerColor, selectedSquare, game]);

  // Handle mistake square animation
  useEffect(() => {
    if (mistakeSquare) {
      mistakeOpacity.setValue(1);
      Animated.timing(mistakeOpacity, {
        toValue: 0,
        duration: 500,
        useNativeDriver: true,
      }).start();
    }
  }, [mistakeSquare, mistakeOpacity]);

  // Handle hint squares animation (avoid runaway loops)
  useEffect(() => {
    if (hintLoopRef.current) {
      hintLoopRef.current.stop();
      hintLoopRef.current = null;
    }

    if (hintSquares && hintSquares.length > 0) {
      hintOpacity.setValue(0);
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(hintOpacity, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(hintOpacity, {
            toValue: 0,
            duration: 500,
            useNativeDriver: true,
          }),
        ]),
        { iterations: 2 }
      );
      hintLoopRef.current = loop;
      loop.start();
    }

    return () => {
      if (hintLoopRef.current) {
        hintLoopRef.current.stop();
        hintLoopRef.current = null;
      }
    };
  }, [hintSquares, hintOpacity]);

  // Update last move when fen changes
  useEffect(() => {
    const history = game.history({ verbose: true });
    if (history.length > 0) {
      const recentMove = history[history.length - 1];
      setLastMove({ from: recentMove.from, to: recentMove.to });
    } else {
      setLastMove(null);
    }
  }, [fen, game]);

  // Play move sound when the board changes due to a move
  useLayoutEffect(() => {
    // Safari requires you to use useLayoutEffect instead of useEffect to play the sound
    const currentFEN = fen;
    const previousFEN = prevFENRef.current;

    if (previousFEN !== currentFEN) {
      const isReset = currentFEN === startingFEN;
      if (!isReset) {
        playMoveSound();
      }
    }

    prevFENRef.current = currentFEN;
  }, [fen, startingFEN]);

  const playMoveSound = () => {
    if (Platform.OS === 'web') {
      const { audioContext, buffer } = webAudioPoolRef.current;
      if (audioContext && buffer) {
        const playSound = () => {
          const source = audioContext.createBufferSource();
          source.buffer = buffer;
          source.connect(audioContext.destination);
          source.start(0);
        };

        if (audioContext.state === 'suspended') {
          audioContext.resume().then(playSound).catch((error) => {
            console.error('Error resuming audio context:', error);
          });
        } else {
          playSound();
        }
      }
    } else if (mobileSoundRef.current) {
      // Mobile platform: Play the preloaded Sound instance
      mobileSoundRef.current.stop(() => {
        mobileSoundRef.current.play((success) => {
          if (!success) {
            console.error('Sound playback failed');
          }
        });
      });
    }
  };

  const checkGameOver = (nextGame) => {
    if (nextGame.isGameOver()) {
      if (nextGame.isCheckmate()) {
        Alert.alert('Checkmate', `Player ${nextGame.turn() === 'w' ? 'Black' : 'White'} wins!`);
      } else if (nextGame.isStalemate()) {
        Alert.alert('Stalemate', 'Draw!');
      } else if (nextGame.isDraw()) {
        Alert.alert('Draw', 'Draw!');
      }
    }
  };

  const getSquareNotation = (row, col) => {
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    return files[col] + (8 - row);
  };

  const isPromotionMove = (from, to) => {
    const movingPiece = game.get(from);
    if (!movingPiece || movingPiece.type !== 'p') return false;
    return to.endsWith('1') || to.endsWith('8');
  };

  const onSquarePress = (row, col) => {
    // Selection is optionally allowed even when moves are not (preselecting during trainer move)
    const selectionEnabled = isInteractive || allowSelectionWhenInactive;
    if (!selectionEnabled) return;

    const square = getSquareNotation(row, col);
    const piece = game.get(square);

    // Selecting a piece is allowed even when it is not the user's turn (preselect use-case)
    if (!selectedSquare) {
      if (piece && piece.color === playerColor) {
        setSelection(square);
      }
      return;
    }

    // If clicking another own piece, switch selection (always allowed when selectionEnabled)
    if (piece && piece.color === playerColor) {
      setSelection(square);
      return;
    }

    // Only allow making a move when interactive AND it is actually the user's turn.
    const canExecuteMove = isInteractive && game.turn() === playerColor;
    if (!canExecuteMove) {
      return;
    }

    // Try to move selected piece to target square
    const possibleMoves = game.moves({ square: selectedSquare, verbose: true });
    const isValid = possibleMoves.some((m) => m.to === square);
    if (!isValid) {
      return;
    }

    const move = { from: selectedSquare, to: square };

    if (isPromotionMove(selectedSquare, square)) {
      const movingPiece = game.get(selectedSquare);
      setPromotionOptions({ ...move, color: movingPiece?.color });
      setPromotionVisible(true);
      return;
    }

    void makeMove(move, undefined);
  };

  const makeMove = async (move, promotion) => {
    const startNonce = selectionNonceRef.current;
    const fromAtStart = selectedSquare;

    // Allow parent handler to control the move (Trainer uses this)
    if (onMove) {
      const result = await onMove(move, promotion);

      if (result && result.MoveHasBeenHandled) {
        // Only clear if the user did not change selection while we awaited trainer work.
        const selectionUnchanged =
          selectionNonceRef.current === startNonce && selectedSquare === fromAtStart;

        if (selectionUnchanged) {
          clearSelection();
        }

        // Ensure promotion UI is closed regardless (but do not wipe selection).
        setPromotionVisible(false);
        setPromotionOptions({});
        return;
      }

      // If parent returned success=false but did not handle, fall through to local move.
      if (result && result.success === false) {
        return;
      }
    }

    // Local move (for standalone boards like OpeningExplorer)
    const nextGame = cloneChessFrom(game);
    const moveResult = nextGame.move({
      from: move.from,
      to: move.to,
      promotion,
    });

    if (!moveResult) {
      return;
    }

    setGame(nextGame);
    clearSelection();
    checkGameOver(nextGame);
  };

  const onPromotionChoice = (promotion) => {
    setPromotionVisible(false);
    void makeMove({ from: promotionOptions.from, to: promotionOptions.to }, promotion);
  };

  const renderSquare = (displayRow, displayCol) => {
    // Flip board for black perspective
    const actualRow = playerColor === 'b' ? 7 - displayRow : displayRow;
    const actualCol = playerColor === 'b' ? 7 - displayCol : displayCol;

    const square = getSquareNotation(actualRow, actualCol);
    const piece = game.get(square);

    const isLightSquare = (actualRow + actualCol) % 2 === 0;
    const isSelected = square === selectedSquare;
    const isMistake = square === mistakeSquare;
    const isHint = Array.isArray(hintSquares) && hintSquares.includes(square);

    const isLastMoveFrom = !!lastMove && square === lastMove.from;
    const isLastMoveTo = !!lastMove && square === lastMove.to;
    const isOnLastMoveSquare = isLastMoveFrom || isLastMoveTo;

    // Base chess colors (keep stable; overlays handle highlights)
    const squareColor = isLightSquare ? '#f0d9b5' : '#b58863';

    return (
      <TouchableOpacity
        key={`${displayRow}-${displayCol}`}
        style={[styles.square, { backgroundColor: squareColor }]}
        onPress={() => onSquarePress(actualRow, actualCol)}
        activeOpacity={0.9}
        accessibilityLabel={`Square ${square}`}
      >
        {/* Highlights UNDER the piece */}
        {isLastMoveFrom && <View pointerEvents="none" style={styles.lastMoveFromOverlay} />}
        {isLastMoveTo && <View pointerEvents="none" style={styles.lastMoveToOverlay} />}

        {isHint && (
          <Animated.View
            pointerEvents="none"
            style={[styles.hintOverlay, { opacity: hintOpacity }]}
          />
        )}

        {/* If selected square is also part of last move, keep last-move fill visible and show selection as a border */}
        {isSelected && (
          <View
            pointerEvents="none"
            style={isOnLastMoveSquare ? styles.selectedOverlayOnLastMove : styles.selectedOverlay}
          />
        )}

        {/* Piece */}
        {piece && <SvgPiece piece={piece} style={styles.piece} />}

        {/* Mistake flash OVER the piece so it is noticeable */}
        {isMistake && (
          <Animated.View
            pointerEvents="none"
            style={[styles.mistakeOverlay, { opacity: mistakeOpacity }]}
          />
        )}
      </TouchableOpacity>
    );
  };

  const renderBoard = () => {
    const rows = new Array(8).fill(null);
    const cols = new Array(8).fill(null);

    return rows.map((_, r) => (
      <View key={`row-${r}`} style={styles.row}>
        {cols.map((__, c) => renderSquare(r, c))}
      </View>
    ));
  };

  return (
    <View style={styles.container}>
      <View style={styles.board}>{renderBoard()}</View>

      {isPromotionVisible && (
        <PromotionModal
          isVisible={isPromotionVisible}
          onSelect={onPromotionChoice}
          color={promotionOptions.color}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    aspectRatio: 1,
    alignSelf: 'center',
  },
  board: {
    flex: 1,
  },
  row: {
    flex: 1,
    flexDirection: 'row',
  },
  square: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  piece: {
    width: '80%',
    height: '80%',
    resizeMode: 'contain',
  },

  // Selected square (amber tint)
  selectedOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(245, 158, 11, 0.18)',
    borderWidth: 2,
    borderColor: 'rgba(245, 158, 11, 0.55)',
  },
  // Selected on last-move square: keep selection clear without hiding last-move fill
  selectedOverlayOnLastMove: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0)', // transparent fill
    borderWidth: 2,
    borderColor: 'rgba(245, 158, 11, 0.70)',
  },

  // Hint squares (blue tint)
  hintOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(59, 130, 246, 0.22)',
    borderWidth: 2,
    borderColor: 'rgba(59, 130, 246, 0.60)',
  },

  // Mistake flash (red)
  mistakeOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(220, 38, 38, 0.35)',
    borderWidth: 2,
    borderColor: 'rgba(220, 38, 38, 0.70)',
  },

  // Last move highlight (LIGHT GRAY)
  lastMoveFromOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(229, 231, 235, 0.14)', // light gray, subtle
    borderWidth: 2,
    borderColor: 'rgba(156, 163, 175, 0.35)',
  },
  lastMoveToOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(229, 231, 235, 0.30)', // light gray, stronger
    borderWidth: 2,
    borderColor: 'rgba(156, 163, 175, 0.80)',
  },
});

Board.propTypes = {
  game: PropTypes.object,
  onMove: PropTypes.func,
  playerColor: PropTypes.oneOf(['w', 'b']),
  mistakeSquare: PropTypes.string,
  hintSquares: PropTypes.arrayOf(PropTypes.string),
  isInteractive: PropTypes.bool,
  allowSelectionWhenInactive: PropTypes.bool,
};

Board.defaultProps = {
  game: new Chess(),
  playerColor: 'w',
  mistakeSquare: null,
  hintSquares: [],
  isInteractive: true,
  allowSelectionWhenInactive: false,
};

export default Board;
